// Single "×" multiplier per model shown in the admin pricing UI. Pure
// display helper — credit math always happens on the backend with the raw
// USD prices, never the multiplier.
//
// Logic:
//   - If the model has a manual `displayMultiplier`, use it. This lets the
//     admin pin friendly round numbers (1, 0.5, 2…) instead of seeing
//     auto-calculated values like 1.67×.
//   - Otherwise, derive it live from `model.prices[baselineTokenType] /
//     baseline.prices[baselineTokenType]`. This way changing the baseline
//     model (or its prices) updates every non-overridden multiplier
//     automatically.
//
// `TOKEN_TYPES` and `TOKEN_TYPE_LABELS` are still exported because the
// per-token price columns + edit modal still surface each price.

import type {
  GlobalPricingConfig,
  ModelPricing,
  TokenType,
} from "./apiTyped";

export const TOKEN_TYPES: TokenType[] = [
  "input",
  "output",
  "cachedRead",
  "cacheCreation",
  "thinking",
];

export const TOKEN_TYPE_LABELS: Record<TokenType, string> = {
  input: "Input",
  output: "Output",
  cachedRead: "Cached read",
  cacheCreation: "Cache write",
  thinking: "Thinking",
};

export type EffectiveMultiplier = {
  value: number;
  source: "override" | "auto";
};

export function effectiveMultiplier(
  model: ModelPricing,
  baseline: ModelPricing | null,
  global: GlobalPricingConfig | null,
): EffectiveMultiplier | null {
  if (
    typeof model.displayMultiplier === "number" &&
    Number.isFinite(model.displayMultiplier)
  ) {
    return { value: model.displayMultiplier, source: "override" };
  }
  const tokenType = global?.baselineTokenType ?? "output";
  if (!baseline) return null;
  const basePrice = baseline.prices[tokenType];
  if (!basePrice || basePrice <= 0) return null;
  const modelPrice = model.prices[tokenType];
  if (modelPrice == null) return null;
  return { value: modelPrice / basePrice, source: "auto" };
}

export function fmtMultiplier(m: EffectiveMultiplier | null): string {
  if (!m) return "—";
  if (m.value >= 100) return `${m.value.toFixed(0)}×`;
  if (m.value >= 10) return `${m.value.toFixed(1)}×`;
  // Strip trailing zeros so "1.00" renders as "1" and "0.50" as "0.5".
  return `${parseFloat(m.value.toFixed(2))}×`;
}

export function creditsPerUsd(global: GlobalPricingConfig | null): number {
  if (!global?.usdPerCredit || global.usdPerCredit <= 0) return 0;
  return 1 / global.usdPerCredit;
}
